"use client";
import React, { useRef } from "react";
import { motion, useInView } from "motion/react";
import AnimatedCard from "@/components/ui/animated-card";
import BorderWrapper from "@/components/ui/border-wrapper";
import Image from "next/image";
import { Carousel, CarouselContent, CarouselItem, CarouselNext, CarouselPrevious } from '@/components/ui/carousel';
import { useLocale, useTranslations } from "next-intl";

const shapeVariants = {
  visible: {
    scale: [0.9, 1, 0.9] as number[],
    transition: { duration: 5, ease: "easeInOut", repeat: Infinity },
  },
} as const;

const containerVariants = {
  hidden: {},
  show: { transition: { staggerChildren: 0.09 } },
} as const;

const itemVariants = {
  hidden: { opacity: 0, y: 24 },
  show: { opacity: 1, y: 0, transition: { duration: 0.5, ease: "easeOut" } },
} as const;

const AboutValues = () => {
  const t = useTranslations("about");
  const locale = useLocale();
  const howWeWork = [
    { number: "01", title: t("values.cards.0.title"), description: t("values.cards.0.description") },
    { number: "02", title: t("values.cards.1.title"), description: t("values.cards.1.description") },
    { number: "03", title: t("values.cards.2.title"), description: t("values.cards.2.description") },
    { number: "04", title: t("values.cards.3.title"), description: t("values.cards.3.description") },
  ];
  const whatWeStandFor = [
    { title: t("values.principles.0.title"), description: t("values.principles.0.description") },
    { title: t("values.principles.1.title"), description: t("values.principles.1.description") },
    { title: t("values.principles.2.title"), description: t("values.principles.2.description") },
    { title: t("values.principles.3.title"), description: t("values.principles.3.description") },
  ];
  const howRef = useRef<HTMLDivElement>(null);
  const definesRef = useRef<HTMLDivElement>(null);
  const howInView = useInView(howRef, { once: true, margin: "0px 0px -80px 0px" });
  const definesInView = useInView(definesRef, { once: true, margin: "0px 0px -80px 0px" });

  return (
    <>
      {/* ── HOW WE WORK ── */}
      <section className="section pt-[50px] pb-[60px] lg:pb-[140px] relative">
        <div
          className="absolute left-[0%] top-[20%] w-[600px] h-[600px] bg-[linear-gradient(180deg,_rgba(135,82,250,0.08)_0%,_rgba(34,12,81,0)_100%)] rounded-full blur-[120px] pointer-events-none animate-shape-pulse"
          />

        <div className="container relative z-10">
          <motion.div
            ref={howRef}
            variants={containerVariants}
            initial="hidden"
            animate={howInView ? "show" : "hidden"}
          >
            <motion.div className="text-center mb-[50px] lg:mb-[70px]" variants={itemVariants}>
              <h2 className="text-[28px] md:text-[40px] lg:text-[48px] font-semibold leading-[1.1] mb-[16px]">
                {t("values.sectionTitle")}
              </h2>
              <p className="text-[15px] lg:text-[16px] font-light leading-[1.7] text-foreground/90 max-w-[500px] mx-auto">
                {t("values.sectionDescription")}
              </p>
            </motion.div>

            {/* Numbered bento grid — no icons, numbers are the visual */}
            {/* MOBILE CAROUSEL */}
            <div className="md:hidden">
              <Carousel aria-label={t("values.sectionTitle")} opts={{ align: "center", loop: false }} className="w-full [&>div]:!overflow-visible">
                <CarouselContent className="-ml-2">
                  {howWeWork.map((item, i) => (
                    <CarouselItem key={i} className="pl-2 basis-[85%]">
                      <AnimatedCard
                        className="rounded-[18px] lg:rounded-[22px] h-full"
                        circleClassName="w-[120px] h-[120px] blur-[70px]"
                        animationDelay={i * 0.07}
                      >
                        <BorderWrapper
                          wrapperClass="rounded-[18px] lg:rounded-[22px] h-full"
                          className="rounded-[18px] lg:rounded-[22px] h-full bg-gradient-to-b from-[#121124] to-[#0e0821] p-[28px] lg:p-[36px] relative overflow-hidden"
                        >
                          <div className="absolute -right-[6px] -top-[14px] text-[110px] font-semibold text-white/[0.04] leading-[1] select-none pointer-events-none">
                            {item.number}
                          </div>
                          <div
                            className="absolute top-0 right-0 w-[120px] h-[120px] bg-theme/10 rounded-full blur-[50px] pointer-events-none animate-shape-pulse"
                            />
                          <div className="text-[11px] font-medium text-theme/60 uppercase tracking-[0.18em] mb-[14px]">
                            {item.number}
                          </div>
                          <h3 className="text-[17px] lg:text-[19px] font-semibold leading-[1.3] mb-[12px]">
                            {item.title}
                          </h3>
                          <p className="text-[15px] lg:text-[16px] font-light leading-[1.7] text-foreground/90">
                            {item.description}
                          </p>
                        </BorderWrapper>
                      </AnimatedCard>
                    </CarouselItem>
                  ))}
                </CarouselContent>
                <div className="flex justify-center items-center mt-[30px] gap-[15px]">
                  <CarouselPrevious className="static translate-x-0 translate-y-0 w-[50px] h-[50px] rounded-full bg-[#4a3290] text-white disabled:bg-[#15092c] cursor-pointer border-white/5 focus:bg-theme focus:text-white" />
                  <CarouselNext className="static translate-x-0 translate-y-0 w-[50px] h-[50px] rounded-full bg-[#4a3290] text-white disabled:bg-[#15092c] cursor-pointer border-white/5 focus:bg-theme focus:text-white" />
                </div>
              </Carousel>
            </div>
            {/* DESKTOP GRID */}
            <div className="hidden md:grid md:grid-cols-2 gap-[14px]">
              {howWeWork.map((item, i) => (
                <motion.div key={i} variants={itemVariants}>
                  <AnimatedCard
                    className="rounded-[18px] lg:rounded-[22px] h-full"
                    circleClassName="w-[120px] h-[120px] blur-[70px]"
                    animationDelay={i * 0.07}
                  >
                    <BorderWrapper
                      wrapperClass="rounded-[18px] lg:rounded-[22px] h-full"
                      className="rounded-[18px] lg:rounded-[22px] h-full bg-gradient-to-b from-[#121124] to-[#0e0821] p-[28px] lg:p-[36px] relative overflow-hidden"
                    >
                      <div className="absolute -right-[6px] -top-[14px] text-[110px] font-semibold text-white/[0.04] leading-[1] select-none pointer-events-none">
                        {item.number}
                      </div>
                      <div
                        className="absolute top-0 right-0 w-[120px] h-[120px] bg-theme/10 rounded-full blur-[50px] pointer-events-none animate-shape-pulse"
                        />
                      <div className="text-[11px] font-medium text-theme/60 uppercase tracking-[0.18em] mb-[14px]">
                        {item.number}
                      </div>
                      <h3 className="text-[17px] lg:text-[19px] font-semibold leading-[1.3] mb-[12px]">
                        {item.title}
                      </h3>
                      <p className="text-[15px] lg:text-[16px] font-light leading-[1.7] text-foreground/90">
                        {item.description}
                      </p>
                    </BorderWrapper>
                  </AnimatedCard>
                </motion.div>
              ))}
            </div>

            {/* Bottom image strip */}
            <motion.div variants={itemVariants} className="mt-[14px]">
              <AnimatedCard
                className="rounded-[18px] lg:rounded-[22px]"
                circleClassName="w-[150px] h-[150px] blur-[90px]"
              >
                <BorderWrapper
                  wrapperClass="rounded-[18px] lg:rounded-[22px]"
                  className="rounded-[18px] lg:rounded-[22px] bg-gradient-to-b from-[#121124] to-[#0e0821] overflow-hidden relative min-h-[220px] lg:min-h-[260px]"
                >
                  <Image
                    src={`/assets/photos/responsibility-${locale}.webp`}
                    alt="Team collaboration and development process"
                    width={1200}
                    height={400}
                    className="w-full h-full object-cover mix-blend-plus-lighter opacity-50 absolute inset-0"
                  />
                  <div className="absolute bottom-[20px] left-[20px] right-[20px] lg:right-auto lg:max-w-[480px] p-[20px] rounded-[14px] bg-[#0a0818]/80 backdrop-blur-md border border-white/10">
                    <p className="text-[15px] lg:text-[16px] font-light leading-[1.7] text-foreground/90">
                      {t("values.craftedDescription")}
                    </p>
                  </div>
                  <div
                    className="absolute top-0 right-0 w-[200px] h-[200px] bg-theme/10 rounded-full blur-[80px] pointer-events-none animate-shape-pulse"
                    />
                </BorderWrapper>
              </AnimatedCard>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* ── WHAT DEFINES US ── */}
      <section className="section pb-[60px] lg:pb-[140px] relative">
        <div
          className="absolute right-[-5%] top-[20%] w-[500px] h-[500px] bg-[linear-gradient(180deg,_rgba(150,128,197,0.1)_0%,_rgba(34,12,81,0)_100%)] rounded-full blur-[100px] pointer-events-none animate-shape-pulse"
          />

        <div className="container relative z-10">
          <motion.div
            ref={definesRef}
            variants={containerVariants}
            initial="hidden"
            animate={definesInView ? "show" : "hidden"}
          >
            <motion.div className="text-center mb-[50px] lg:mb-[70px]" variants={itemVariants}>
              <h2 className="text-[28px] md:text-[40px] lg:text-[48px] font-semibold leading-[1.1] mb-[16px]">
                {t("values.principlesTitle")}
              </h2>
              <p className="text-[15px] lg:text-[16px] font-light leading-[1.7] text-foreground/90 max-w-[500px] mx-auto">
                {t("values.principlesDescription")}
              </p>
            </motion.div>
          </motion.div>

          <div className="max-w-[860px] mx-auto">
            {whatWeStandFor.map((item, i) => (
              <div
                key={i}
                className="sticky mt-[2px] lg:mt-[14px]"
                style={{ top: `${20 + i * 64}px`, zIndex: 10 + i }}
              >
                <AnimatedCard
                  className="rounded-[16px]"
                  circleClassName="w-[100px] h-[100px] blur-[60px]"
                  animationDelay={i * 0.06}
                >
                  <BorderWrapper
                    wrapperClass="rounded-[16px]"
                    className="rounded-[16px] bg-gradient-to-b from-[#121124] to-[#0e0821] relative overflow-hidden"
                  >
                    <div className="absolute left-0 top-0 bottom-0 w-[3px] bg-gradient-to-b from-theme via-[#622aad] to-transparent" />
                    <div className="pl-[28px] pr-[24px] lg:pr-[40px] py-[22px] lg:py-[28px] grid lg:grid-cols-[1fr_1.4fr] gap-[8px] lg:gap-[60px] items-center">
                      <h3 className="text-[16px] lg:text-[18px] font-semibold leading-[1.3]">
                        {item.title}
                      </h3>
                      <p className="text-[15px] lg:text-[16px] font-light leading-[1.7] text-foreground/90">
                        {item.description}
                      </p>
                    </div>
                  </BorderWrapper>
                </AnimatedCard>
              </div>
            ))}
          </div>
        </div>
      </section>
    </>
  );
};

export default AboutValues;
